import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { FIRM, SERVICES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you. A partner will reply within a business day.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not send your message. Please try again or email us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-16">
          <div className="overline rule-teal">Contact</div>
          <h1 className="font-display text-5xl sm:text-6xl mt-6 leading-[1.02] text-[#0B2E5C] max-w-3xl">
            Start the conversation.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#475569] leading-relaxed font-light">
            Tell us about your audit, advisory or compliance need. A partner will
            reach out within one business day.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-testid="contact-body">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#E2E8F0] p-8 md:p-12">
              <div className="overline">Send a message</div>
              <h2 className="font-display text-3xl text-[#0B2E5C] mt-3">
                We respond within one business day.
              </h2>

              <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-wider text-[#475569]">
                      Full name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={form.name}
                      onChange={onChange}
                      className="mt-2 rounded-sm h-11 focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#475569]">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      className="mt-2 rounded-sm h-11 focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-[#475569]">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      className="mt-2 rounded-sm h-11 focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-xs uppercase tracking-wider text-[#475569]">
                      Company
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={form.company}
                      onChange={onChange}
                      className="mt-2 rounded-sm h-11 focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                      data-testid="contact-input-company"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">
                    Service of interest
                  </Label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm({ ...form, service: v })}
                  >
                    <SelectTrigger
                      data-testid="contact-select-service"
                      className="mt-2 rounded-sm h-11 focus:ring-[#2BA8B8]"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.slug}
                          value={s.title}
                          data-testid={`contact-service-option-${s.slug}`}
                        >
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="Other / Not sure" data-testid="contact-service-option-other">
                        Other / Not sure
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs uppercase tracking-wider text-[#475569]">
                    How can we help? *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={onChange}
                    placeholder="A few lines about your business and what you need."
                    className="mt-2 rounded-sm focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                    data-testid="contact-input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="w-full md:w-auto rounded-sm bg-[#0B2E5C] hover:bg-[#082142] text-white h-12 px-8"
                >
                  {loading ? "Sending..." : "Send message"}
                  {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
                <p className="text-xs text-[#475569]">
                  By submitting, you agree we may contact you about your enquiry.
                  We never share your information.
                </p>
              </form>
            </div>
          </div>

          {/* Office details */}
          <div className="lg:col-span-5 space-y-8" data-testid="contact-office-info">
            <div className="bg-[#0B2E5C] text-white p-10">
              <div className="overline !text-[#2BA8B8]">Mogadishu office</div>
              <h3 className="font-display text-3xl mt-3">Visit us</h3>
              <ul className="mt-8 space-y-5 text-white/85 font-light">
                <li className="flex gap-4">
                  <MapPin className="h-5 w-5 text-[#2BA8B8] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span>
                    {FIRM.address}
                    <br />
                    {FIRM.city}
                  </span>
                </li>
                <li className="flex gap-4">
                  <Phone className="h-5 w-5 text-[#2BA8B8] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <a href={FIRM.phoneHref} className="hover:text-white" data-testid="contact-info-phone">
                    {FIRM.phone}
                  </a>
                </li>
                <li className="flex gap-4">
                  <Mail className="h-5 w-5 text-[#2BA8B8] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <a href={FIRM.emailHref} className="hover:text-white" data-testid="contact-info-email">
                    {FIRM.email}
                  </a>
                </li>
                <li className="flex gap-4">
                  <Clock className="h-5 w-5 text-[#2BA8B8] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span>{FIRM.hours}</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-[#E2E8F0] overflow-hidden">
              <iframe
                title="Mizan Audit office location"
                src="https://www.google.com/maps?q=Taleh+Road+Mogadishu+Somalia&output=embed"
                className="w-full h-72 grayscale-[20%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
